import { createReducer } from 'redux-starter-kit';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import { selectChecklistMap } from '../../../domain/checklists/checklistsReducer';
import {
  addChecklist,
  addPublishChecklist, beginAddChecklist, cancelChecklistAdd, cancelChecklistTitle, fetchChecklistPageData, fetchChecklistsPageData,
  removePublishChecklist, updateChecklistCategoryConfig, updateChecklistConfig, updateChecklistTitle,
} from './checklistConfigUiActions';
import { selectChecklistsConfigMap } from '../../../domain/checklistsConfig/checklistsConfigReducer';
import { selectChecklistCategoryMap } from '../../../domain/checklistCategories/checklistCategoriesReducer';
import { selectChecklistItemMap } from '../../../domain/checklistItems/checklistItemsReducer';


/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the checklist being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.checklistsConfig.editState;

/**
 * A selector for the associated state for the 'ADD_CHECKLIST' action.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the inspection reason being edited.
 * @example const editState = selectEditState(state);
 */
export const selectAddChecklistState = state => state.via.ui.checklistsConfig.addChecklistState;

/**
 * A selector for finding the current publishing status.
 * @param {object} state The redux state of the application.
 * @returns An array of checklist codes.
 */
export const selectLoadingActiveStatus = state => state.via.ui.checklistsConfig.pendingPublishChecklists;

/**
 * A selector that returns the state of the 'FETCH_CHECKLISTS_PAGE_DATA' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectChecklistsPageDataRequest = (state) => {
  const requestId = state.via.ui.checklistsConfig.requestChecklistsPageDataId;
  if (!requestId) return null;
  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_CHECKLIST_PAGE_DATA' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectChecklistPageDataRequest = (state) => {
  const requestId = state.via.ui.checklistsConfig.requestChecklistPageDataId;
  if (!requestId) return null;
  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'ADD_CHECKLIST' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectAddChecklistRequest = state => selectRequest(state, 'ADD_CHECKLIST');

/**
 * A selector that returns the state of the 'UPDATE_CHECKLIST_TITLE' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectUpdateChecklistTitleRequest = state => selectRequest(state, 'UPDATE_CHECKLIST_TITLE');

/**
 * A selector that returns the state of the 'REORDER_CHECKLIST_CATEGORIES_OR_ITEMS' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectReorderChecklistRequest = state => selectRequest(state, 'REORDER_CHECKLIST_CATEGORIES_OR_ITEMS');

/**
 * A selector that returns the state of the 'ADD_CATEGORY_TO_CHECKLIST' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectAddCategoryChecklistRequest = state => selectRequest(state, 'ADD_CATEGORY_TO_CHECKLIST');

/**
 * A selector that returns the state of the 'ADD_ITEM_TO_CHECKLIST' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 */
export const selectAddItemChecklistRequest = state => selectRequest(state, 'ADD_ITEM_TO_CHECKLIST');

/**
 * A selector that returns all checklists.
 * Null if not currently loaded, otherwise a list of the checklists.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the checklists.
 */
export const selectAllChecklists = (state) => {
  const checklistMap = selectChecklistMap(state);
  return checklistMap == null ? [] : Object.values(checklistMap);
};

/**
 * A selector that returns all checklist categories.
 * Null if not currently loaded, otherwise a list of the checklists.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the checklists.
 */
export const selectAllChecklistCategories = (state) => {
  const checklistCatMap = selectChecklistCategoryMap(state);
  return checklistCatMap == null ? [] : Object.values(checklistCatMap);
};

/**
 * A selector that returns all checklist items.
 * Null if not currently loaded, otherwise a list of the checklists.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the checklists.
 */
export const selectAllChecklistItems = (state) => {
  const checklistItemMap = selectChecklistItemMap(state);
  return checklistItemMap == null ? [] : Object.values(checklistItemMap);
};

/**
 * A selector that returns the currently loaded configuration for all checklists.
 * Null if not currently loaded, otherwise a list of the checklists.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the checklists.
 */
export const selectAllChecklistsConfig = (state) => {
  const checklistMap = selectChecklistsConfigMap(state);
  return checklistMap == null ? [] : Object.values(checklistMap);
};

const initialEditingState = {
  checklistId: 0,
  checklistData: null,
  validationMessages: ({
    title: '',
    messages: [],
    visible: false,
  }),
};

const initialAddChecklistState = {
  description: '',
  popOverVisible: false,
};

const checklistsInitialState = {
  editState: initialEditingState, // Is a inspection reason being edited/created.
  addChecklistState: initialAddChecklistState, // Add checklist popover
  pendingPublishChecklists: [], // Checklists being submitted for publishing.
  requestChecklistsPageDataId: null, // Request id for loading checklists page data.
  requestChecklistPageDataId: null, // Request id for loading checklist page data.
};

/**
 * A reducer for handling the UI state changes for the Checklists Configuration screen.
 */
const checklistsConfigReducer = createReducer(checklistsInitialState, {
  // A new request has been made to load all the checklists page data
  [fetchChecklistsPageData]: (state, action) => ({
    ...state,
    requestChecklistsPageDataId: action.payload,
  }),

  // A new request has been made to load all the checklist edit page data
  [fetchChecklistPageData]: (state, action) => ({
    ...state,
    requestChecklistPageDataId: action.payload.requestId,
    editState: {
      ...state.editState,
      checklistId: action.payload.checklistId,
      checklistData: action.payload.checklistData,
    },
  }),

  // A checklist is being published. So add it to state.
  [addPublishChecklist]: (state, action) => ({
    ...state,
    pendingPublishChecklists: [...state.pendingPublishChecklists, action.payload],
  }),

  // A checklist has finished publishing. Remove it from state.
  [removePublishChecklist]: (state, action) => ({
    ...state,
    pendingPublishChecklists: state.pendingPublishChecklists.filter(code => `${code}` !== `${action.payload}`),
  }),

  // Checklist updated (in various ways).
  [updateChecklistConfig]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      checklistData: action.payload,
    },
  }),

  // Update checklist category.
  [updateChecklistCategoryConfig]: (state, action) => {
    const checklistObj = action.payload;
    const { categoryId } = checklistObj;
    const { category } = checklistObj;
    const categories = [...state.editState.checklistData.checkListCategories];
    categories[categories.findIndex(cat => cat.categoryCode === categoryId)] = category;

    return {
      ...state,
      editState: {
        ...state.editState,
        checklistData: {
          ...state.editState.checklistData,
          checkListCategories: categories,
        },
      },
    };
  },

  // A checklist title is being changed. So add it to state.
  [updateChecklistTitle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      checklistData: {
        ...state.editState.checklistData,
        description: action.payload.description,
      },
    },
  }),

  // A checklist title update is cancelled.
  [cancelChecklistTitle]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      checklistData: {
        ...state.editState.checklistData,
        description: action.payload,
      },
    },
  }),

  // Add checklist popover - start
  [beginAddChecklist]: state => ({
    ...state,
    addChecklistState: {
      description: '',
      popOverVisible: true,
    },
  }),

  // Add checklist popover - save
  [addChecklist]: (state, action) => ({
    ...state,
    addChecklistState: {
      description: action.payload,
      popOverVisible: false,
    },
  }),

  // Add checklist popover - cancel
  [cancelChecklistAdd]: state => ({
    ...state,
    addChecklistState: {
      description: '',
      popOverVisible: false,
    },
  }),
});

export default checklistsConfigReducer;
